/**
 * Improve the heading structure of third-party product widgets.
 *
 * Selleasy and Tolstoy own and re-render their widget markup, so this listens
 * for their delayed rendering and observes only the relevant widget elements
 * for subsequent updates.
 */
(function () {
  'use strict';

  // Fail closed on unsupported browsers. This customization must never affect
  // the product form or any other storefront functionality.
  if (
    typeof window.addEventListener !== 'function' ||
    typeof document.addEventListener !== 'function' ||
    typeof document.querySelectorAll !== 'function' ||
    typeof document.createElement !== 'function' ||
    typeof window.MutationObserver !== 'function' ||
    typeof window.WeakMap !== 'function'
  ) {
    return;
  }

  // Selleasy "Shop the collection (List)" widget.
  var widgetSelector =
    '.Product__Info lb-upsell-widget[widget="widget#cpal"]';
  var appBlockSelector = '.Product__Info .lb-widget-cpal';
  var widgetObservers = new window.WeakMap();
  var appBlockObservers = new window.WeakMap();

  // Tolstoy shoppable-video carousel.
  var tolstoyCarouselSelector = 'tolstoy-carousel';
  var tolstoyCarouselObservers = new window.WeakMap();

  function isElement(element) {
    return (
      element &&
      element.nodeType === 1 &&
      typeof element.matches === 'function' &&
      typeof element.querySelector === 'function' &&
      typeof element.querySelectorAll === 'function'
    );
  }

  function replaceWithHeading(element, tagName) {
    if (
      !isElement(element) ||
      !element.parentNode ||
      element.localName === tagName
    ) {
      return;
    }

    var parent = element.parentNode;
    var heading = document.createElement(tagName);
    var isLink = element.getAttribute('role') === 'link';

    Array.prototype.forEach.call(element.attributes, function (attribute) {
      if (attribute.name !== 'role' && attribute.name !== 'tabindex') {
        heading.setAttribute(attribute.name, attribute.value);
      }
    });

    // Divs have no default margin, so keep the widget's existing appearance.
    heading.style.margin = '0';

    if (isLink) {
      // Preserve the product-title interaction without replacing the native
      // heading role with role="link".
      var link = document.createElement('span');
      link.setAttribute('role', 'link');

      if (element.hasAttribute('tabindex')) {
        link.setAttribute('tabindex', element.getAttribute('tabindex'));
      }

      while (element.firstChild) {
        link.appendChild(element.firstChild);
      }

      heading.appendChild(link);
    } else {
      while (element.firstChild) {
        heading.appendChild(element.firstChild);
      }
    }

    parent.replaceChild(heading, element);
  }

  // Selleasy: use an h2 for the widget title and h3 for product titles.
  function updateHeadings(widget) {
    try {
      replaceWithHeading(widget.querySelector('.lb-addon-title'), 'h2');

      Array.prototype.forEach.call(
        widget.querySelectorAll(
          '.lb-flat-card .lb-card-title:not(.lb-card-title-skeleton)'
        ),
        function (title) {
          replaceWithHeading(title, 'h3');
        }
      );
    } catch (error) {
      // Selleasy owns this DOM and may change it without notice. If that
      // happens, leave its markup untouched instead of affecting the page.
    }
  }

  // Tolstoy: use an h2 for the carousel title.
  function updateTolstoyHeading(carousel) {
    try {
      replaceWithHeading(
        carousel.querySelector('.tolstoy-carousel-title'),
        'h2'
      );
    } catch (error) {
      // Tolstoy owns this DOM and may change it without notice. If that
      // happens, leave its markup untouched instead of affecting the page.
    }
  }

  // Tolstoy renders asynchronously and may replace its title during re-renders.
  function watchTolstoyCarousel(carousel) {
    if (
      !isElement(carousel) ||
      !carousel.matches(tolstoyCarouselSelector)
    ) {
      return;
    }

    updateTolstoyHeading(carousel);

    if (!tolstoyCarouselObservers.has(carousel)) {
      var observer = new window.MutationObserver(function () {
        updateTolstoyHeading(carousel);
      });

      // Tolstoy renders asynchronously inside this custom element. Keeping the
      // observer scoped here avoids watching unrelated storefront changes.
      observer.observe(carousel, {
        childList: true,
        subtree: true
      });

      tolstoyCarouselObservers.set(carousel, observer);
    }
  }

  // Selleasy may update or replace its generated widget markup.
  function watchWidget(widget) {
    if (
      !isElement(widget) ||
      !widget.matches(widgetSelector)
    ) {
      return;
    }

    updateHeadings(widget);

    if (!widgetObservers.has(widget)) {
      var observer = new window.MutationObserver(function () {
        updateHeadings(widget);
      });

      observer.observe(widget, {
        childList: true,
        subtree: true
      });

      widgetObservers.set(widget, observer);
    }
  }

  function findWidget(root) {
    if (!isElement(root)) {
      return null;
    }

    if (root.matches(widgetSelector)) {
      return root;
    }

    return root.querySelector(widgetSelector);
  }

  function watchAppBlock(appBlock) {
    if (!isElement(appBlock) || appBlockObservers.has(appBlock)) {
      return;
    }

    var existingWidget = findWidget(appBlock);

    if (existingWidget) {
      watchWidget(existingWidget);
      return;
    }

    // Observe only the empty Selleasy app block while waiting for delayed
    // initialization. Never observe the full document.
    var observer = new window.MutationObserver(function () {
      try {
        var widget = findWidget(appBlock);

        if (widget) {
          watchWidget(widget);
          observer.disconnect();
          appBlockObservers.delete(appBlock);
        }
      } catch (error) {
        observer.disconnect();
        appBlockObservers.delete(appBlock);
      }
    });

    observer.observe(appBlock, {
      childList: true,
      subtree: true
    });

    appBlockObservers.set(appBlock, observer);
  }

  function findAppBlocks(root) {
    try {
      if (isElement(root) && root.matches(appBlockSelector)) {
        watchAppBlock(root);
      }

      if (root && typeof root.querySelectorAll === 'function') {
        Array.prototype.forEach.call(
          root.querySelectorAll(appBlockSelector),
          watchAppBlock
        );
      }
    } catch (error) {
      // A failed optional customization must remain a no-op.
    }
  }

  function findTolstoyCarousels(root) {
    try {
      if (isElement(root) && root.matches(tolstoyCarouselSelector)) {
        watchTolstoyCarousel(root);
      }

      if (root && typeof root.querySelectorAll === 'function') {
        Array.prototype.forEach.call(
          root.querySelectorAll(tolstoyCarouselSelector),
          watchTolstoyCarousel
        );
      }
    } catch (error) {
      // A failed optional customization must remain a no-op.
    }
  }

  // Selleasy-specific render event.
  window.addEventListener('lbWidgetRendered', function (event) {
    try {
      watchWidget(event && event.detail && event.detail.widgetSrcElement);
    } catch (error) {
      // Do not allow an unexpected third-party event payload to escape.
    }
  });

  // Shopify emits this when replacing a section in the theme editor.
  document.addEventListener('shopify:section:load', function (event) {
    findAppBlocks(event && event.target);
    findTolstoyCarousels(event && event.target);
  });

  // Initialize the Selleasy customization.
  findAppBlocks(document);

  // Initialize the Tolstoy customization.
  findTolstoyCarousels(document);
})();
